import React from 'react';

import { UnhandledError } from './application/errors/UnhandledError';
import { AppBuilder, OfflineAppBuilder } from './application/features/App/components/AppBuilder';
import { Logger } from './application/Logging/Logger';
import { isOfflineStartup, isOnlineStartup, readStartupOptions } from './config/startupOptions';
import { createTranslationService } from './translates/services/translationService';
import type { ProjectVM } from '@ael/core-api';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { RawIntlProvider } from 'react-intl';
import { polyfill } from 'smoothscroll-polyfill';

// Based on https://stackoverflow.com/questions/51229742/javascript-window-scroll-behavior-smooth-not-working-in-safari
polyfill();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: Infinity,
      networkMode: 'always',
    },
  },
});

const ApplicationEntryPoint = () => {
  const options = readStartupOptions(window.location.search);

  // Default intl context for everything rendered before StudentApp mounts its own
  // RawIntlProvider with the project culture (which overrides this one). Without it,
  // the startup error overlays (e.g. TrackingNotInitializedErrorOverlay) crash on useIntl().
  const defaultIntl = React.useMemo(() => {
    // window.courseConfig is only set in offline/exported builds despite the non-optional typing
    const offlineCultureCode = (window.courseConfig as ProjectVM | undefined)?.settings.cultureCode;
    return createTranslationService(offlineCultureCode ?? options.cultureCode ?? 'en-us');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.StrictMode>
      <RawIntlProvider value={defaultIntl}>
        <ErrorBoundary
          FallbackComponent={UnhandledError}
          onReset={() => window.location.reload()}
          onError={(error) => {
            Logger.trackException(error);
          }}
        >
          <Helmet>
            {/* TODO add light/dark theme */}
            {/* <html className="light" /> */}
          </Helmet>
          <QueryClientProvider client={queryClient}>
            {isOnlineStartup(options) && <AppBuilder options={options} />}
            {isOfflineStartup(options) && <OfflineAppBuilder options={options} />}
          </QueryClientProvider>
        </ErrorBoundary>
      </RawIntlProvider>
    </React.StrictMode>
  );
};

class App extends React.Component {
  render() {
    return <ApplicationEntryPoint />;
  }
}

export default App;
